import { Button } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { FORM_TYPES, FormDTO } from '@/interfaces';
import { CIOMS_FORM_FIELDS } from './constants/cioms';
import DynamicFormFields from './dynamic-form-fields';
import FixedFormFields from './fixed-form-fields';
import { AntdForm, Buttons, FormContainer } from './styles';
import { convertAPIDataToFormData, convertFormDataToAPIData } from './formDataHelper';
import { SAE_AESI_FORM_FIELDS } from './constants/sae-aesi';
import { PREGNANCY_FORM_FIELDS } from './constants/pregnancy';
import { success, warning } from '@/services/notification';
import { ADVERSE_EVENTS_FORM_FIELDS } from './constants/adverse-events';

type FormType = {
  data: FormDTO;
  onUpdate: (data: FormDTO) => void;
  isUpdating: boolean;
};

// NOTE (2026-09-01): SAE_AESI's event list used to have one combined
// "Grade/Severity" field (event[].gradeSeverity, e.g. 'MILD', 'SEVERE').
// It has been split into two independent fields, event[].grade and
// event[].severity, which the form now renders and edits. The two helpers
// below make that change backward compatible with existing data:
//  - backfillGradeAndSeverityFromLegacyValue derives starting values for
//    the two new fields from the old combined value, purely for display
//    when opening an existing record that predates the split, so the user
//    isn't shown blank Grade/Severity fields for data that was actually
//    captured.
//  - preserveLegacyGradeSeverity re-attaches the original
//    event[].gradeSeverity value onto the submitted payload, since it is
//    no longer part of any rendered form field and would otherwise be
//    silently dropped the next time the record is saved. This keeps the
//    original historical value intact in the database indefinitely - no
//    destructive migration is performed.
const LEGACY_GRADE_SEVERITY_TO_GRADE: Record<string, string> = {
  MILD: 'GRADE_1',
  MODERATE: 'GRADE_2',
  SEVERE: 'GRADE_3',
  LIFE_THREATENING: 'GRADE_4',
  DEATH: 'GRADE_5',
};

const backfillGradeAndSeverityFromLegacyValue = (formData: Record<string, any>) => {
  if (Array.isArray(formData?.event)) {
    formData.event = formData.event.map((event: any) => {
      if (!event || !event.gradeSeverity) {
        return event;
      }
      const newEvent = { ...event };
      if (newEvent.grade === undefined) {
        newEvent.grade = LEGACY_GRADE_SEVERITY_TO_GRADE[event.gradeSeverity];
      }
      if (newEvent.severity === undefined) {
        newEvent.severity = event.gradeSeverity;
      }
      return newEvent;
    });
  }
};

const preserveLegacyGradeSeverity = (
  originalData: Record<string, any> | undefined,
  newData: Record<string, any>
) => {
  const originalEvents: any[] = originalData?.event || [];
  if (Array.isArray(newData?.event)) {
    newData.event = newData.event.map((event: any, index: number) => {
      const legacyValue = originalEvents[index]?.gradeSeverity;
      if (event && legacyValue !== undefined && event.gradeSeverity === undefined) {
        return { ...event, gradeSeverity: legacyValue };
      }
      return event;
    });
  }
};

const Form = ({ data, onUpdate, isUpdating }: FormType) => {
  const [form] = AntdForm.useForm();
  const [hasShownWarning, setShowWarning] = useState(false);

  const dynamicFormFields = useMemo(() => {
    const { type } = data;

    switch (type) {
      case FORM_TYPES.CIOMS:
        return CIOMS_FORM_FIELDS;
      case FORM_TYPES.PREGNANCY:
        return PREGNANCY_FORM_FIELDS;
      case FORM_TYPES.SAE_AESI:
        return SAE_AESI_FORM_FIELDS;
      case FORM_TYPES.ADVERSE_EVENTS:
        return ADVERSE_EVENTS_FORM_FIELDS;
      default:
        throw new Error(`Unexpected form type ${type}`);
    }
  }, [data]);

  const initialValues = useMemo(() => {
    const formData = convertAPIDataToFormData(dynamicFormFields, data.data);
    if (data.type === FORM_TYPES.SAE_AESI) {
      backfillGradeAndSeverityFromLegacyValue(formData);
    }
    return {
      ...formData,
      // saeNumber lives on the Form record itself (like subject/center),
      // not inside data.data, so it's merged in separately here.
      saeNumber: data.saeNumber,
    };
  }, [data]);

  const handleFormSubmit = async () => {
    await form.validateFields();
    // Pull saeNumber out before converting the rest of the fields, since it
    // is a top-level Form field and must not be nested under `data`.
    const { saeNumber, ...dynamicValues } = form.getFieldsValue();
    const newData = convertFormDataToAPIData(dynamicFormFields, dynamicValues);
    if (data.type === FORM_TYPES.SAE_AESI) {
      preserveLegacyGradeSeverity(data.data, newData);
    }
    onUpdate({
      ...data,
      saeNumber,
      data: newData,
    });
  };

  useEffect(() => {
    form.resetFields();
  }, [data.data]);

  const onValuesChange = (_: any, allValues: any) => {
    if ([FORM_TYPES.PREGNANCY, FORM_TYPES.SAE_AESI].includes(data.type)) {
      showWarningForSAEOrPregnancyExistingForm(allValues);
    }
  };

  const showWarningForSAEOrPregnancyExistingForm = (allValues: any) => {
    if (hasShownWarning) return;
    // @ts-ignore
    if (data?.data?.['reportType'] === 'INITIAL') {
      warning(
        'You have changed field entries, please remember to QC and complete medical review fields again'
      );
      setShowWarning(true);
    }
  };

  return (
    <FormContainer>
      <AntdForm
        onValuesChange={onValuesChange}
        layout={'vertical'}
        form={form}
        disabled={data.locked || isUpdating}
        initialValues={initialValues}
      >
        <FixedFormFields
          subjectId={data.subject}
          centerId={data.center}
          ip={data.data?.ip}
          title={data.type === FORM_TYPES.CIOMS ? 'FORM INFORMATION' : undefined}
        />
        <DynamicFormFields fields={dynamicFormFields} />
      </AntdForm>
      {!data.locked && (
        <Buttons>
          <Button type="primary" onClick={handleFormSubmit} loading={isUpdating}>
            {isUpdating ? 'Updating...' : 'Submit'}
          </Button>
        </Buttons>
      )}
    </FormContainer>
  );
};

export default Form;
